import React from 'react'

import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'

import MainModal from './common/MainModal'
import { IconUser } from '../icons/IconUser'
import { IconEmailImg } from '../icons/IconEmailImg'
import { IconDate } from '../icons/IconDate'
import { NationalityIconImg } from '../icons/NationalityIconImg'
import { IconError } from '../icons/IconError'
import { InputUploadMemberPictureRow } from '../inputs/InputUploadMemberPictureRow'
import { InputNewMemberRow } from '../inputs/InputNewMemberRow'
import { InputCheckMemberInfo } from '../inputs/InputCheckMemberInfo'
import { IconRole } from '../icons/IconRole'
import { IconUserName } from '../icons/IconUserName'
import { IconHorseImg } from '../icons/IconHorseImg'
import { IconColoringImg } from '../icons/IconColoringImg'

import useToasterHelper from '../../helpers/ToasterHelper'
import { getFilteredPaperwork, saveUserDocuments } from '../../helpers/documents'
import { getConvertedData } from '../../models/interface.helper'

import FirebaseStorageService from '../../services/storageService'
import FirebaseApp from '../../services/firebaseApp'
import FirestoreService from '../../services/firestoreService'
import UserService from '../../services/firebase-service/users/userService'
import { httpService } from '../../services/httpService'

import useProfileHook from '../../hooks/users/competitor/profile/useProfileHook'
import useTeamMembers from '../../hooks/useTeamMembers'

import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setRidersTeamMembersUpdates } from '../../store/paperworks/paperworkSlice'

import { RiderTeamMemberModel } from '../../models/rider-team-member/riderTeamMember.model'
import { EventReviewPublishModel } from '../../models/event-review-publish/event-review-publish.model'

import { IRiderTeamMemberInterface } from '../../models/rider-team-member/riderTeamMember.interface'
import { ITeamMember } from '../../models/users/user.interface'
import { IInputUserPaperworkKey, IInputUserPaperworkData } from '../home/types/inputData'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'

import { CountryList } from '../../fakeData/countryList'
import { disciplineData } from '../../fakeData/disciplineList'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST, USER_DEF_PASS } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'

const TEAM_MEMBER_ROLES = CONST.UI.TEAM_MEMBERS_ROLES
const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

type CreateNewTeamMemberPaperworkModalProps = {
  show: boolean
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    rider: IRegistrationByDayInterface
  }
}

const icons = {
  userFullName: <IconUser />,
  userName: <IconUserName />,
  email: <IconEmailImg />,
  coloring: <IconColoringImg />,
  date: <IconDate />,
  nationality: <NationalityIconImg />,
  discipline: <IconHorseImg />,
  role: <IconRole />,
}

export const CreateNewTeamMemberPaperworkModal = ({
  dataToPassOn,
  handleModal,
}: CreateNewTeamMemberPaperworkModalProps) => {
  const { saveRidersTeamMembers } = useTeamMembers()

  const ROLES = Object.values(TEAM_MEMBER_ROLES)
    .filter((role) => role !== TEAM_MEMBER_ROLES.RIDER)
    .map((value) => ({
      value,
      label: value,
    }))
  const [loading, setLoading] = React.useState(false)
  const userIcon = '/assets/img/User2.png'
  const [picture, setPicture] = React.useState('')
  const dispatch = useAppDispatch()

  const [inputData, setInputDate] = React.useState<IInputUserPaperworkData>({
    userFullName: {
      name: 'userFullName',
      value: '',
      required: true,
      placeholder: 'Enter name...',
      type: 'text',
      label: 'Name',
    },
    userName: {
      name: 'userName',
      value: '',
      required: true,
      placeholder: 'Enter user name...',
      type: 'text',
      label: 'User Name',
    },
    email: {
      name: 'email',
      value: '',
      required: true,
      placeholder: 'Enter address...',
      type: 'email',
      label: 'Email address',
    },
    date: {
      name: 'date',
      value: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toString(),
      required: true,
      placeholder: 'Select date...',
      type: 'date',
      label: 'Date of birth',
    },
    nationality: {
      name: 'nationality',
      value: '',
      required: false,
      placeholder: 'Select nationality...',
      type: 'select',
      label: 'Nationality',
      selectData: CountryList,
    },
    discipline: {
      name: 'discipline',
      value: '',
      required: false,
      placeholder: 'Select discipline....',
      type: 'select',
      label: 'Discipline',
      selectData: disciplineData,
    },
    role: {
      name: 'role',
      value: '',
      required: true,
      placeholder: 'Select role....',
      type: 'select',
      label: 'Role',
      selectData: ROLES,
    },
  })

  const [isValid, setIsValid] = React.useState(false)
  const [isConfirmed, setIsConfirmed] = React.useState(false)
  const { getModelledUserDetails } = useProfileHook()
  const toastFunctions = useToasterHelper()
  const [imgLoading, setImgLoading] = React.useState(false)

  const exibitorR = useAppSelector((store) => store.exhibitor.registration)

  const { rider } = dataToPassOn

  React.useEffect(() => {
    const checkValidity = () => {
      for (const key in inputData) {
        const field = inputData[key as IInputUserPaperworkKey]
        if (field.required && !field.value) {
          return false
        }
      }
      return true
    }

    setIsValid(isConfirmed && checkValidity())
  }, [inputData, isConfirmed])

  const onChangeHandler = (key: string, value: string) => {
    const newInputData = { ...inputData }
    if (newInputData[key as IInputUserPaperworkKey]) {
      newInputData[key as IInputUserPaperworkKey].value = value
      newInputData[key as IInputUserPaperworkKey].hasError = false
      setInputDate(newInputData)
    }
  }

  const onSubmit = async (
    isSetLoading?: boolean
  ): Promise<{
    id: string | null
  } | null> => {
    // const { email, name, date, nationality, discipline } = props.dataToPassOn.inputData

    if (!rider?.userId) {
      toastFunctions.error({ message: 'User id not found' })
      return null
    }

    try {
      setLoading(true)

      const userCredential = await createUserWithEmailAndPassword(
        FirebaseApp.auth,
        `${inputData.email.value}`.toLowerCase(),
        USER_DEF_PASS
      )
      const user = userCredential.user

      await updateProfile(userCredential.user, {
        displayName: `${inputData.userFullName.value}`,
      })

      const resp = await httpService({
        url: `create_user`,
        method: 'POST',
        data: {
          uuid: user.uid,
          userFullName: `${inputData.userFullName.value}`,
          userName: `${inputData.userName.value}`,
          email: `${inputData.email.value}`.toLowerCase(),
          userType: 'competitor',
          createdForOtherUser: true,
        },
      })

      if (!resp.data.user) {
        alert('No User')
        return null
      }
      const respUser = resp.data.user

      const dataTOUpdate: ITeamMember = {
        defaultRole: inputData.role.value || '',
        memberAddress: '',
        memberCountry: '',
        memberDob: inputData.date.value,
        memberEmail: respUser.userEmail,
        memberId: respUser.id,
        memberName: respUser.userFullName,
        memberPhoneNumber: respUser.userPhoneNumber,
        memberProfilePicture: respUser.userProfilePicture,
        memberRole: CONST.UI.TEAM_MEMBERS_ROLES.TEAM_MEMBER,
        memberUserName: respUser.userName,
        selected: true,
        signedStatus: 'Not sent',
      }

      const structuredData = getModelledUserDetails(respUser)

      if (Array.isArray(structuredData.userTeamMembers)) {
        structuredData.userTeamMembers.push(dataTOUpdate)
      } else {
        structuredData.userTeamMembers = [dataTOUpdate]
      }

      await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, respUser.id, {
        ...structuredData,
        userProfilePicture: picture,
        userName: `${inputData.userName.value}`,
        userDOB: inputData.date.value,
        teamMemberRole: inputData.role.value || '',
        teamMemberName: `${inputData.userFullName.value}`,
        userNationality: inputData.nationality?.value || '',
      })

      respUser.userProfilePicture = picture

      if (!exibitorR) return null

      const ridersTeamMembersToAdd: IRiderTeamMemberInterface = new RiderTeamMemberModel({
        mailLog: [],
        userId: exibitorR.userId,

        eventId: exibitorR.eventId || '',
        eventName: exibitorR.eventName || null,

        riderId: rider.riderId,
        riderEmail: rider.riderEmail,
        riderNameNGram: rider.riderNameNGram || [],
        registrationDocId: rider.registrationDocId,
        riderDob: `${inputData.date.value}` || null,
        riderName: `${inputData.userFullName.value}`,
        riderCountry: rider.riderCountry,
        riderProfilePicture: rider.riderProfilePicture,

        teamMemberDob: `${inputData.date.value}` || null,
        teamMemberName: `${inputData.userFullName.value}`,
        teamMemberRole: `${inputData.role.value || ''}`,
        teamMemberId: respUser.id || null,
        teamMemberEmail: respUser.userEmail || null,
        teamMemberProfilePicture: respUser.userProfilePicture || '',

        horseId: rider.horseId,
        horseName: rider.horseName,
        horseProfilePicture: rider.horseProfilePicture || null,

        modified: new Date().toString(),
        created: new Date().toString(),
      }).toObject()

      const publishedEventInDbSnapShot = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        rider.eventId || ''
      )

      const publishedEventInDb = getConvertedData(
        EventReviewPublishModel.fromFirestoreDoc(publishedEventInDbSnapShot).toObject()
      )

      const filteredPaperworkDocuments = getFilteredPaperwork(
        publishedEventInDb?.EventPaperwork ?? null
      )

      const userInDb = await UserService.getUserById(rider.userId)

      const updatedTeamMembers = await saveRidersTeamMembers({
        ridersTeamMembersToAdd: ridersTeamMembersToAdd ? [ridersTeamMembersToAdd] : [],
      })

      await saveUserDocuments({
        filteredPaperworkDocuments,
        ridersTeamMembersToAdd: [...(updatedTeamMembers?.ridersTeamMembersToAdd ?? [])],
        ridersTeamMembersToUpdate: [...(updatedTeamMembers?.ridersTeamMembersToUpdate ?? [])],
        eventDetails: publishedEventInDb.EventDetails,
        competitor: userInDb,
      })

      dispatch(setRidersTeamMembersUpdates())
      handleModal(false, MODAL_CONSTS.CREATE_NEW_TEAM_MEMBER_PAPERWORK)
      toastFunctions.success({ message: MESSAGES_CONST.TEAM_MEMBER_CREATED })
      return {
        id: respUser.id || null,
      }
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      return null
    } finally {
      isSetLoading && setLoading(false)
    }
  }

  const updateProfilePic = async (event: any) => {
    const files = event.target.files
    const file = files[0]
    const PATH = CONST.DATA.STORAGE.USERS.COMPETITOR_PROFILE_IMAGE_URL.PREFIX

    if (!file) return toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })

    setImgLoading(true)

    try {
      const downloadUrl = await FirebaseStorageService.uploadFile(
        file,
        `${PATH}/${new Date().getTime()}`,
        (percent: number) => {
          if (0) console.log(percent)
        }
      )
      if (downloadUrl) {
        setPicture(`${downloadUrl}`)
      }

      toastFunctions.success({ message: MESSAGES_CONST.PIC_UPDATED })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setImgLoading(false)
    }
  }

  return (
    <>
      <MainModal
        title="Add new team member"
        show={true}
        type="CREATE_NEW_TEAM_MEMBER_PAPERWORK"
        size="md"
        onTransitionEnd={() => null}
        titleClassName="!font-normal"
        buttons={[
          {
            loading,
            label: 'Create',
            bgClass: 'bg-SeabiscuitMainThemeColor',
            onClick: onSubmit,
            className: 'outline-none !w-full',
            textClass: 'text-white',
            disabled: !isValid,
          },
          {
            label: 'CANCEL',
            bgClass: 'bg-SeabiscuitLightThemeColor',
            className: 'outline-none !w-full',
            borderClass: 'border border-transparent',
            textClass: 'text-SeabiscuitLightTextColor',
            onClick: () => handleModal(false, MODAL_CONSTS.CREATE_NEW_TEAM_MEMBER_PAPERWORK),
          },
        ]}
      >
        <div className={'flex flex-col gap-4 mt-4'}>
          <div className={'flex flex-col gap-2 border-b border-[#D3DAEE] pb-4'}>
            <InputUploadMemberPictureRow
              title={'Profile picture'}
              isLoading={imgLoading}
              picture={picture}
              onchangeHandler={updateProfilePic}
              userIcon={userIcon}
            />
            {Object.keys(inputData).map((key) => (
              <InputNewMemberRow
                key={key}
                value={inputData[key as IInputUserPaperworkKey].value}
                onChangeHandler={onChangeHandler}
                placeholder={inputData[key as IInputUserPaperworkKey].placeholder}
                inputName={inputData[key as IInputUserPaperworkKey].name}
                name={inputData[key as IInputUserPaperworkKey].label}
                isError={inputData[key as IInputUserPaperworkKey].hasError}
                icon={
                  icons[inputData[key as IInputUserPaperworkKey].name as IInputUserPaperworkKey]
                }
                isRequired={inputData[key as IInputUserPaperworkKey].required}
                type={inputData[key as IInputUserPaperworkKey].type}
                selectData={inputData[key as IInputUserPaperworkKey].selectData || []}
              />
            ))}
          </div>

          <InputCheckMemberInfo
            isChecked={isConfirmed}
            onClick={() => setIsConfirmed(!isConfirmed)}
            text={
              'By ticking this box I confirm that I have been given permission to create a Pegasus\n' +
              '                account on behalf of this person, and I am authorized to agree to Pegasus’ terms and\n' +
              '                conditions on their behalf, as well as register them for events.'
            }
          />

          <div className={'flex mt-4 gap-4 bg-[#F6F7FB] py-[12px] px-[20px] rounded-[12px]'}>
            <div className={'mt-1'}>
              <IconError />
            </div>

            <p className={'flex-1 text-[#122B46] text-[14px] m-0'}>
              The email address listed above will be notified of this account creation and provided
              with login details to join Pegasus and complete their profile.
            </p>
          </div>
        </div>
      </MainModal>
    </>
  )
}
